import { Box } from "@mui/material";
import FilterMenu from "../../../../components/filters/FilterMenu";
import SearchAndOrFilter from "../../../../components/filters/SearchAndOrFilter";
import TimeFilter from "../../../../components/filters/TimeFilter";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { LogicNameFilter } from "../../../../utils/constants";
import IntegrationRowFilter from "../../../posture/steps/components/IntegrationRowFilter";
import { NHITypeIconsAndLabels } from "../utils";
import {
  ALL_OPTION_NAME,
  filterName,
  futureTimeValues,
  pastTimeValues,
} from "./utils";

export const pastTimeOptions = Object.entries(pastTimeValues).map(
  ([name, info]) => ({
    name: name,
    label: info.label,
    displayName: info.label,
  })
);

const futureTimeOptions = Object.entries(futureTimeValues).map(
  ([name, info]) => ({
    name: name,
    label: info.label,
    displayName: info.label,
  })
);

const getNHITypeOptions = (fetchedEntities) => {
  const types = new Set(
    fetchedEntities.map((entity) => entity.subtype).filter(Boolean)
  );
  return [
    {
      name: ALL_OPTION_NAME,
      label: "All NHI types",
      displayName: "All",
    },
    ...Array.from(types).map((type) => {
      if (!NHITypeIconsAndLabels[type]) {
        // Provide a fallback for types that don't have an icon defined
        return {
          name: type,
          label: type,
          displayName: type,
        };
      }

      const [Icon, label] = NHITypeIconsAndLabels[type];
      return {
        name: type,
        label: (
          <Box display="flex" gap={1} alignItems="center">
            <SvgToIcon IconLogo={Icon} width={"18px"} />
            {label}
          </Box>
        ),
        displayName: label,
      };
    }),
  ];
};

const rotationEnabledOptions = [
  {
    name: "true",
    label: "Enabled",
    displayName: "Enabled",
  },
  {
    name: "false",
    label: "Disabled",
    displayName: "Disabled",
  },
];

const getPoliciesOptions = (fetchedEntities) => {
  const policiesWithSource = new Map();

  fetchedEntities.forEach((entity) => {
    if (entity?.aws_common_data?.policy_names) {
      entity.aws_common_data.policy_names.forEach((policyName) => {
        if (policyName) {
          const uniqueKey = `${policyName}__aws`;
          policiesWithSource.set(uniqueKey, {
            policyName: policyName,
            source: "aws",
          });
        }
      });
    }
  });

  return [
    {
      name: ALL_OPTION_NAME,
      label: "All policies",
      displayName: "All",
    },
    ...Array.from(policiesWithSource.entries())
      .map(([uniqueKey, policyData]) => ({
        name: uniqueKey,
        label: (
          <IntegrationRowFilter
            name={policyData.source}
            label={policyData.policyName}
          />
        ),
        displayName: policyData.policyName,
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName)),
  ];
};

const nhiPrivilegedInADOptions = [
  {
    name: "yes",
    label: "Yes",
    displayName: "Yes",
  },
  {
    name: "no",
    label: "No",
    displayName: "No",
  },
];

const getConnectedEmailOptions = (fetchedEntities, userEntities) => {
  const emailToUser = new Map();

  const userMap = new Map(
    userEntities?.map((u) => [u.original_unique_id, u]) || []
  );

  for (const entity of fetchedEntities) {
    const email = entity?.connected_email;

    if (email && !emailToUser.has(email)) {
      const matchedUser = userMap.get(email);
      if (matchedUser) {
        emailToUser.set(email, matchedUser);
      }
    }
  }

  const options = Array.from(emailToUser.entries()).map(([email, user]) => {
    return {
      name: email,
      label: user?.display_name || email,
      displayName: user?.display_name || email,
    };
  });

  options.sort((a, b) => a.displayName.localeCompare(b.displayName));

  return [
    {
      name: ALL_OPTION_NAME,
      label: "All users",
      displayName: "All",
    },
    ...options,
  ];
};

export const getNHIFilters = (params) => {
  let filters = [
    <FilterMenu
      filterName={"Type"}
      fieldName={filterName.NHI_TYPE}
      options={getNHITypeOptions(params.fetchedEntities)}
      appliedFilters={params.appliedFilters}
      setAppliedFilters={params.setAppliedFilters}
    />,
    <FilterMenu
      filterName={"Related User"}
      fieldName={filterName.NHI_CONNECTED_EMAIL}
      options={getConnectedEmailOptions(
        params.fetchedEntities,
        params.userEntities
      )}
      appliedFilters={params.appliedFilters}
      setAppliedFilters={params.setAppliedFilters}
      showSearch={true}
      filterLogic={LogicNameFilter.LOGIC_OR}
    />,
    <TimeFilter
      filterName={"Last Used"}
      fieldName={filterName.NHI_LAST_USED}
      options={pastTimeOptions}
      appliedFilters={params.appliedFilters}
      setAppliedFilters={params.setAppliedFilters}
      filterLogic={params.filterAndOrLogics[filterName.NHI_LAST_USED]}
      setFilterLogic={params.setFilterAndOrLogics}
    />,
    <FilterMenu
      filterName={"Expiration Date"}
      fieldName={filterName.EXPIRATION_DATE}
      options={futureTimeOptions}
      appliedFilters={params.appliedFilters}
      setAppliedFilters={params.setAppliedFilters}
      singleSelect
    />,
    <FilterMenu
      filterName={"Rotation"}
      fieldName={filterName.NHI_ROTATION_ENABLED}
      options={rotationEnabledOptions}
      appliedFilters={params.appliedFilters}
      setAppliedFilters={params.setAppliedFilters}
    />,
    <SearchAndOrFilter
      filterName={"Policies"}
      fieldName={filterName.NHI_POLICIES}
      options={getPoliciesOptions(params.fetchedEntities)}
      appliedFilters={params.appliedFilters}
      setAppliedFilters={params.setAppliedFilters}
      filterLogic={params.filterAndOrLogics[filterName.NHI_POLICIES]}
      setFilterLogic={params.setFilterAndOrLogics}
    />,
  ];

  if (params?.integrations?.includes("microsoft_active_directory")) {
    filters.push(
      <FilterMenu
        filterName={"Privileged in AD"}
        fieldName={filterName.NHI_PRIVILEGED_IN_AD}
        options={nhiPrivilegedInADOptions}
        appliedFilters={params.appliedFilters}
        setAppliedFilters={params.setAppliedFilters}
      />
    );
  }

  return filters;
};
